/**
 * TextRenderer - Renders text elements
 * Implements text nodes with font styling, alignment, and editable content
 */

import Konva from 'konva';
import { BaseRenderer } from '../core/BaseRenderer';
import { EditableElement } from '../editing/types';

export class TextRenderer extends BaseRenderer {
  private textNode: Konva.Text | null = null;
  private selectionRect: Konva.Rect | null = null;
  private _isSelectedState: boolean = false;

  constructor(element: EditableElement) {
    super(element);
  }

  /**
   * Render the text element
   */
  render(): Konva.Node {
    const { width, height, data } = this.element;
    
    // Selection rectangle (only visible when selected)
    this.selectionRect = new Konva.Rect({
      width: width,
      height: height,
      fill: 'transparent',
      stroke: '#0066FF',
      strokeWidth: 1,
      dash: [5, 5],
      visible: false,
      listening: false,
    });
    
    // Text content
    const textContent = (data?.text || 'Double-click to edit').trim() || 'Double-click to edit';
    this.textNode = new Konva.Text({
      text: textContent,
      width: width,
      height: height,
      fontSize: data?.fontSize || 16,
      fontFamily: data?.fontFamily || 'Arial',
      fontStyle: this.getFontStyle(data?.fontStyle),
      fill: data?.fill || '#000000',
      align: data?.align || 'left',
      verticalAlign: 'top',
      wrap: 'word',
      lineHeight: data?.lineHeight || 1.2,
      letterSpacing: data?.letterSpacing || 0,
    });
    
    this._node.add(this.selectionRect);
    this._node.add(this.textNode);
    
    return this._node;
  }

  /**
   * Convert font style to Konva format
   */
  private getFontStyle(fontStyle?: string): string {
    if (!fontStyle) return 'normal';
    
    // Konva expects: 'normal', 'bold', 'italic', or 'bold italic'
    // But for fontStyle attribute it's: 'normal' or 'italic'
    // Bold is handled by fontWeight, but Konva's fontStyle accepts combined values
    return fontStyle;
  }

  /**
   * Update the text element with new properties
   */
  update(updates: Partial<EditableElement>): void {
    this.updateElementData(updates);
    
    const { width, height, data } = this.element;
    
    // Update selection rect
    if (this.selectionRect) {
      if (updates.width !== undefined || updates.height !== undefined) {
        this.selectionRect.width(width);
        this.selectionRect.height(height);
      }
    }
    
    // Update text
    if (this.textNode) {
      if (updates.width !== undefined || updates.height !== undefined) {
        this.textNode.width(width);
        this.textNode.height(height);
      }
      
      if (updates.data) {
        if (updates.data.text !== undefined) {
          const textContent = (updates.data.text || 'Double-click to edit').trim() || 'Double-click to edit';
          this.textNode.text(textContent);
        }
        if (updates.data.fontSize !== undefined) {
          this.textNode.fontSize(updates.data.fontSize);
        }
        if (updates.data.fontFamily !== undefined) {
          this.textNode.fontFamily(updates.data.fontFamily);
        }
        if (updates.data.fontStyle !== undefined) {
          this.textNode.fontStyle(this.getFontStyle(updates.data.fontStyle));
        }
        if (updates.data.fill !== undefined) {
          this.textNode.fill(updates.data.fill);
        }
        if (updates.data.align !== undefined) {
          this.textNode.align(updates.data.align);
        }
        if (updates.data.lineHeight !== undefined) {
          this.textNode.lineHeight(updates.data.lineHeight);
        }
        if (updates.data.letterSpacing !== undefined) {
          this.textNode.letterSpacing(updates.data.letterSpacing);
        }
      }
    }
  }

  /**
   * Set dimensions and update internal nodes
   */
  setDimensions(width: number, height: number): void {
    super.setDimensions(width, height);
    
    if (this.selectionRect) {
      this.selectionRect.width(width);
      this.selectionRect.height(height);
    }
    
    if (this.textNode) {
      this.textNode.width(width);
      this.textNode.height(height);
    }
  }

  /**
   * Set selected state
   */
  setSelected(selected: boolean): void {
    super.setSelected(selected);
    this._isSelectedState = selected;
    
    if (this.selectionRect) {
      this.selectionRect.visible(selected);
    }
  }

  /**
   * Hide text (for text editing overlay)
   */
  hideText(): void {
    if (this.textNode) {
      this.textNode.visible(false);
    }
  }

  /**
   * Show text
   */
  showText(): void {
    if (this.textNode) {
      this.textNode.visible(true);
    }
  }

  /**
   * Get text content
   */
  getText(): string {
    return this.element.data?.text || '';
  }

  /**
   * Get font size
   */
  getFontSize(): number {
    return this.element.data?.fontSize || 16;
  }

  /**
   * Get font family
   */
  getFontFamily(): string {
    return this.element.data?.fontFamily || 'Arial';
  }

  /**
   * Get text color
   */
  getTextColor(): string {
    return this.element.data?.fill || '#000000';
  }

  /**
   * Get text alignment
   */
  getTextAlign(): 'left' | 'center' | 'right' {
    return this.element.data?.align || 'left';
  }

  /**
   * Destroy the renderer
   */
  destroy(): void {
    this.textNode = null;
    this.selectionRect = null;
    super.destroy();
  }
}
